import type {GridFilterModel} from "@mui/x-data-grid-premium";
import {
    SUPPLIER_ENTITY_TYPES,
    type SupplierEntityListParams,
    type SupplierEntityType,
} from "~/pages/supplier-360/types/Supplier360Types";
import {asNumber, asStringArray} from "~/pages/supplier-360/components/gridFilterValues";

const ENTITY_TYPES = new Set<SupplierEntityType>(SUPPLIER_ENTITY_TYPES);

export type EntityColumnFilters = Pick<SupplierEntityListParams,
    | 'entity_type'
    | 'se_country_code_a2'
    | 'confidence_gte'
    | 'confidence_lte'
    | 'spend_gte'
    | 'spend_lte'
    | 'business_unit'>;

/** Numeric columns only expose `>=` / `<=` — the endpoint's `__gte` / `__lte` range filters (§2.1). */
function setRange(
    params: EntityColumnFilters,
    operator: string,
    value: number,
    gte: 'confidence_gte' | 'spend_gte',
    lte: 'confidence_lte' | 'spend_lte',
) {
    if (operator === '>=') params[gte] = value;
    if (operator === '<=') params[lte] = value;
}

/**
 * Maps DataGrid filter items to `supplier_360/entity/` query params (§2.1).
 * Only operators the API supports are applied: exact / `__in` for country, exact for entity_type,
 * `>=` / `<=` for Score and Spend, and exact / list for Business unit.
 */
export function entityListParamsFromFilterModel(model: GridFilterModel): EntityColumnFilters {
    const params: EntityColumnFilters = {};

    for (const item of model.items) {
        if (!item.field) continue;

        if (item.field === 'confidence' || item.field === 'spend') {
            const value = asNumber(item.value);
            if (value === undefined) continue;
            if (item.field === 'confidence') setRange(params, item.operator, value, 'confidence_gte', 'confidence_lte');
            else setRange(params, item.operator, value, 'spend_gte', 'spend_lte');
            continue;
        }

        const values = asStringArray(item.value);
        if (values.length === 0) continue;

        if (item.field === 'entity_type' && (item.operator === 'is' || item.operator === 'equals')) {
            const type = values[0] as SupplierEntityType;
            if (ENTITY_TYPES.has(type)) params.entity_type = type;
        }

        if (item.field === 'se_country' && (item.operator === 'equals' || item.operator === 'is' || item.operator === 'isAnyOf')) {
            if (values.length === 1) params.se_country_code_a2 = values[0];
            else if (values.length > 1) params.se_country_code_a2 = values;
        }

        if (item.field === 'business_units' && (item.operator === 'equals' || item.operator === 'isAnyOf')) {
            params.business_unit = values;
        }
    }

    return params;
}
